/** SizeTab — Electrical-room dimensions, auto-layout, tray defaults, plan view. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Question, Trash, Door, ArrowCounterClockwise, ArrowClockwise, PushPin } from "@phosphor-icons/react";
import HelpModal from "@/components/r3d/HelpModal";
import OpeningsEditor from "@/components/r3d/OpeningsEditor";
import ElevationViews from "@/components/ElevationViews";
import { Field } from "./_shared";
import Iso3D from "./size/Iso3D";
import PlanView from "./size/PlanView";
import { orientedDimensions } from "@/lib/orientation";
import { fmtLenShort } from "@/lib/units";
import { wallFlushMargin } from "@/components/elevation/wallGeometry";

function Stat2({ label, value, accent, warn }) {
  return (
    <div className="border border-white/10 p-2">
      <div className="label-tag !text-[10px]">{label}</div>
      <div className={`mt-1 ${accent ? "text-[#00E5FF]" : warn ? "text-[#FF8A85]" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

export default function SizeTab({ project, update, onUndo, onRedo, canUndo, canRedo }) {
  const metric = project.unit_system === "metric";
  const unitLen = metric ? "m" : "in";
  const unitWeight = metric ? "kg" : "lbs";
  /** Unit-toggle ONLY swaps the display preference — `_in` fields are
   *  always stored as inches (and `weight_lbs` always pounds) so the
   *  geometry remains consistent across the 2D plan, elevation, and 3D
   *  renderers. Toggling between systems no longer rescales any data. */
  const setUnit = (sys) => update({ unit_system: sys });
  /** Display ↔ storage helpers (storage is always imperial). */
  const IN_PER_M = 39.3701;
  const LB_PER_KG = 2.20462;
  const lenOut = (inches) => metric ? (inches || 0) / IN_PER_M : (inches || 0);
  const lenStore = (display) => metric ? Number(display) * IN_PER_M : Number(display);
  const wtOut = (lbs) => metric ? (lbs || 0) / LB_PER_KG : (lbs || 0);
  const wtStore = (display) => metric ? Number(display) * LB_PER_KG : Number(display);
  // Footprint is computed in inches² regardless of display unit.
  const footprint = (project.length_in || 0) * (project.width_in || 0);
  const [layoutMeta, setLayoutMeta] = useState(project.layout_meta || null);
  const [violations, setViolations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [trayOptions, setTrayOptions] = useState(null);
  const [trayMode, setTrayMode] = useState(false);
  const [partitionMode, setPartitionMode] = useState(false);
  // v1.95 — equipment installation-side filter. "all" shows everything
  // (interior, exterior, and through). "interior" / "exterior" only show
  // matching items. Through (straddle) items always show because they
  // belong to both contexts.
  const [installSide, setInstallSide] = useState("all");
  // v2.00 — Plan-view mode toggle (matches the Wall Elevations toolbar
  // visual language). "select" = no mode active (drag to move equipment),
  // "tray" = tray drawing mode, "partition" = interior wall draw,
  // "equipment" (v2.01) = pick an unplaced item and click to place. In
  // EXTERIOR mode, equipment placement targets the ROOF surface; in
  // Interior/All mode it targets the floor.
  const [equipmentMode, setEquipmentMode] = useState(false);
  const [pickedItemId, setPickedItemId] = useState("");
  const planMode = equipmentMode ? "equipment"
                 : trayMode ? "tray"
                 : partitionMode ? "partition"
                 : "select";
  const setPlanMode = (m) => {
    setTrayMode(m === "tray");
    setPartitionMode(m === "partition");
    setEquipmentMode(m === "equipment");
    if (m !== "equipment") setPickedItemId("");
  };
  const unplacedItems = (project.equipment_list || []).filter((i) => !i?.position);
  const pickedItem = unplacedItems.find((it) => it.id === pickedItemId) || null;
  /**
   * v2.01 — handle a click on the plan view in Equipment mode.
   *  • installSide === "exterior" → place on ROOF
   *    (mount_surface: "roof", installation_side: "exterior", mount_height_in = H)
   *  • installSide === "basement" → place in BASEMENT pit (v2.12)
   *    (mount_surface: "basement", installation_side: "basement", mount_height_in = -basement_depth_in)
   *  • installSide === "interior" or "all" → place on FLOOR
   *    (mount_surface: "floor", installation_side: "interior", mount_height_in = 0)
   */
  const handlePlanEquipmentPlace = (x_in, y_in) => {
    if (!pickedItem) return;
    const isExterior = installSide === "exterior";
    const isBasement = installSide === "basement";
    const od = orientedDimensions(pickedItem);
    const L = project.length_in || 0;
    const W = project.width_in  || 0;
    const H = project.depth_in  || 96;
    const basementD = project.basement_depth_in || 0;
    const clampX = Math.max(0, Math.min(L - od.wallSize, Math.round(x_in)));
    const clampY = Math.max(0, Math.min(W - od.thickness, Math.round(y_in)));
    const next = (project.equipment_list || []).map((it) => it.id !== pickedItem.id ? it : {
      ...it,
      mount_height_in:
        isExterior ? H :
        isBasement ? -basementD : 0,
      mount_surface:
        isExterior ? "roof" :
        isBasement ? "basement" : "floor",
      installation_side:
        isExterior ? "exterior" :
        isBasement ? "basement" : "interior",
      position: { x_in: clampX, y_in: clampY,
                  w_in: od.wallSize, h_in: od.thickness },
    });
    update({ equipment_list: next });
    const label = isExterior ? "ROOF (exterior)"
                : isBasement ? `BASEMENT (sub-floor, ${basementD}″ below)`
                : "floor";
    toast.success(`Placed ${pickedItem.tag} on ${label}`);
    setPickedItemId("");
    setEquipmentMode(false);
  };
  // v2.00 — Plan-view layout pins (named checkpoints). Lightweight: stored
  // in component state, not persisted across reloads. Mirrors the Wall
  // Elevations Pin feature.
  const [layoutPins, setLayoutPins] = useState([]);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinName, setPinName] = useState("");
  const pinCurrent = () => {
    if (!pinName.trim()) return;
    setLayoutPins((arr) => [...arr, {
      id: `pin-${Date.now()}`,
      name: pinName.trim(),
      snapshot: JSON.parse(JSON.stringify(project)),
      at: new Date().toISOString(),
    }]);
    toast.success(`Pinned layout: ${pinName.trim()}`);
    setPinName(""); setPinDialogOpen(false);
  };
  const restorePin = (pin) => {
    update({
      equipment_list: pin.snapshot.equipment_list || [],
      cable_trays: pin.snapshot.cable_trays || [],
      room_partitions: pin.snapshot.room_partitions || [],
      wall_openings: pin.snapshot.wall_openings || [],
    });
    toast.success(`Restored: ${pin.name}`);
  };
  // v2.06 — thick-walls A/B test toggle. The user is comparing realistic
  // (`thickWalls=false`, ~14 px) vs bold diagrammatic walls
  // (`thickWalls=true`, ~40-50 px). One will be removed after the user
  // picks a winner.
  const [thickWalls, setThickWalls] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [openingsOpen, setOpeningsOpen] = useState(false);
  const [showDims, setShowDims] = useState(false);
  /** Unified selection model — `{kind: "equipment"|"tray"|"partition"|null, id}`. */
  const [selection, setSelection] = useState({ kind: null, id: null });
  /** v1.79 — id of item that JUST moved via a counter edit. Cleared after
   *  ~600 ms so PlanView can paint a brief glow pulse during the glide. */
  const [recentlyEdited, setRecentlyEdited] = useState(null);
  /** v1.81 — live-preview position for the currently-edited dim counter.
   *  As the user types into a counter input, this state is updated each
   *  keystroke so the rect + dim arrows glide BEFORE commit. Cleared on
   *  Enter/✓ commit, Escape, or blur-with-empty. Shape: { id, x_in, y_in }
   *  for equipment, or { kind: "tray", id, dx, dy } for trays. */
  const [previewPos, setPreviewPos] = useState(null);

  useEffect(() => {
    api.get("/trays/options").then(({ data }) => setTrayOptions(data)).catch(() => {});
  }, []);

  // v2.13 — ESC cancels active plan-view mode + clears selection. Saves
  // the user from a stuck-mode trap (e.g. partition first-click placed
  // but they want to bail without committing the second click).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" && e.key !== "Esc") return;
      const t = e.target;
      const tag = (t?.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) return;
      if (trayMode || partitionMode || equipmentMode) {
        setTrayMode(false);
        setPartitionMode(false);
        setEquipmentMode(false);
        setPickedItemId("");
        toast.info("Cancelled — ESC");
        return;
      }
      if (selection?.kind) {
        setSelection({ kind: null, id: null });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [trayMode, partitionMode, equipmentMode, selection]);

  // v2.14 — clamp partition endpoints any time the room is resized
  // smaller so partitions never extend past the enclosure boundary. Runs
  // 250ms after the user stops typing in length/width inputs so transient
  // values (e.g. "1" on the way to "120") don't trigger a destructive
  // clamp. If a partition collapses to length < 12″ after clamping it
  // is dropped — and the user is warned via toast.
  useEffect(() => {
    const L = project?.length_in || 0;
    const W = project?.width_in  || 0;
    const parts = project?.room_partitions || [];
    if (!parts.length || !L || !W) return;
    const t = setTimeout(() => {
      let mutated = false;
      let droppedCount = 0;
      const next = parts.flatMap((p) => {
        const x1 = Math.max(0, Math.min(L, p.x1));
        const y1 = Math.max(0, Math.min(W, p.y1));
        const x2 = Math.max(0, Math.min(L, p.x2));
        const y2 = Math.max(0, Math.min(W, p.y2));
        const clamped = (x1 !== p.x1 || y1 !== p.y1 || x2 !== p.x2 || y2 !== p.y2);
        if (!clamped) return [p];
        mutated = true;
        const lenIn = Math.hypot(x2 - x1, y2 - y1);
        if (lenIn < 12) { droppedCount += 1; return []; }
        return [{ ...p, x1, y1, x2, y2 }];
      });
      if (mutated) {
        update({ room_partitions: next });
        if (droppedCount > 0) {
          toast.error(`${droppedCount} partition${droppedCount === 1 ? "" : "s"} dropped (collapsed below 12″ after room resize)`);
        } else {
          toast.info("Partition endpoints clamped to new room bounds");
        }
      }
    }, 250);
    return () => clearTimeout(t);
    // We intentionally depend ONLY on the room dims — adding `update`
    // or `project.room_partitions` causes an infinite loop because
    // `update` writes back into the same field we depend on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.length_in, project?.width_in]);

  /* v1.79 — heal stale `position.w_in / position.h_in` on existing
   * equipment whose orientedDimensions no longer match what's stored.
   * This fires once per project load. Reason: equipment placed BEFORE
   * v1.61 (or flipped without a subsequent counter-edit) carries legacy
   * `w_in/h_in` baked into `position` that drifts from the current
   * `axis_map`. Plan rect uses oriented dims and renders correctly, but
   * `computeClearances` reads `p.x_in / p.y_in` against the oriented
   * bbox — and any code path that still falls back to `p.w_in/h_in`
   * (3D measurement helpers, elevation overlays) computes against the
   * stale value. Fix: silently rewrite `position.w_in/h_in` to match
   * `orientedDimensions(item)`. No-op for items already in sync. */
  useEffect(() => {
    if (!project?.id || !Array.isArray(project.equipment_list)) return;
    let dirty = false;
    const L = project.length_in || 0;
    const W = project.width_in  || 0;
    const healed = project.equipment_list.map((it) => {
      if (!it?.position) return it;
      const od = orientedDimensions(it);
      const p = it.position;
      const expectedW = od.wallSize;
      const expectedH = od.thickness;
      // v1.91 — ALSO re-clamp x_in/y_in into the room with the freshly
      // oriented bbox. XFMR-003 in Eroom-1 was stored at x_in=871 with
      // pos.w_in=37 (legacy) but oriented wallSize=29.1 → right edge at
      // 900.1 > L=900. The visual rect poked past the wall in plan + 3D.
      // Clamping here brings it back inside.
      const maxX = Math.max(0, L - expectedW);
      const maxY = Math.max(0, W - expectedH);
      const clampedX = Math.max(0, Math.min(maxX, p.x_in || 0));
      const clampedY = Math.max(0, Math.min(maxY, p.y_in || 0));
      const wDrift = Math.abs((p.w_in || 0) - expectedW);
      const hDrift = Math.abs((p.h_in || 0) - expectedH);
      const xDrift = Math.abs((p.x_in || 0) - clampedX);
      const yDrift = Math.abs((p.y_in || 0) - clampedY);
      if (wDrift < 0.5 && hDrift < 0.5 && xDrift < 0.5 && yDrift < 0.5) return it;
      dirty = true;
      return { ...it, position: {
        ...p, w_in: expectedW, h_in: expectedH,
        x_in: clampedX, y_in: clampedY,
      } };
    });
    if (dirty) update({ equipment_list: healed });
  }, [project?.id]);

  // Listen for tray-rename events bubbled from the SVG plan view.
  useEffect(() => {
    const onRename = (e) => {
      const { id, newId } = e.detail || {};
      if (!id || !newId) return;
      const next = (project.cable_trays || []).map((t) =>
        t.id === id ? { ...t, id_display: newId } : t,
      );
      update({ cable_trays: next });
      toast.success(`Tray renamed → ${newId}`);
    };
    window.addEventListener("eengdes:tray-rename", onRename);
    return () => window.removeEventListener("eengdes:tray-rename", onRename);
  }, [project.cable_trays, update]);

  // E2E hook — lets Playwright drive the plan-view selection
  // programmatically. SVG pointer events from synthetic browser-driver
  // clicks are unreliable, so we expose a small `window` setter that
  // mirrors what a user click on plan-equip-* / plan-tray-* would do.
  useEffect(() => {
    window.__plan_select_for_test = (kind, id) => setSelection({ kind, id });
    return () => { delete window.__plan_select_for_test; };
  }, []);

  const trayDefaults = project.tray_defaults || {
    tray_type: "Ladder", width_in: 12, height_in: 4,
    material: "Aluminum", fill_limit_pct: 50,
  };
  const setTrayDefaults = (patch) => update({ tray_defaults: { ...trayDefaults, ...patch } });

  const runAutoLayout = async () => {
    const llCount = (project.equipment_list || []).length;
    if (llCount === 0) {
      toast.error("Equipment list is empty — add equipment first. Click Help for the workflow.");
      return;
    }
    const placedCount = (project.equipment_list || []).filter((i) => i?.position).length;
    if (placedCount > 0 && !window.confirm(`${placedCount} item(s) already have positions. Auto-layout will overwrite them. Continue?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/layout/auto`);
      const next = (data.equipment_list && data.equipment_list.length >= llCount) ? data.equipment_list : (project.equipment_list || []);
      update({ equipment_list: next, layout_meta: data.metadata });
      setLayoutMeta(data.metadata);
      setViolations(data.violations || []);
      if ((data.violations || []).length === 0) toast.success("Auto-layout — NEC 110.26 enforced");
      else toast.warning(`Layout placed with ${data.violations.length} note(s)`);
    } catch { toast.error("Auto-layout failed"); } finally { setBusy(false); }
  };

  const runAutoTrays = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/trays/auto-route`);
      update({ cable_trays: data.segments });
      setViolations(data.violations || []);
      if ((data.violations || []).length === 0) toast.success("Cable trays auto-routed");
      else toast.warning(`Trays routed with ${data.violations.length} note(s)`);
    } catch { toast.error("Tray auto-route failed"); } finally { setBusy(false); }
  };

  const clearLayout = () => {
    update({
      equipment_list: (project.equipment_list || []).map((i) => ({ ...i, position: null })),
      layout_meta: null, cable_trays: [],
    });
    setLayoutMeta(null); setViolations([]);
    toast.success("Layout & trays cleared");
  };
  const removeTray = (id) => update({
    cable_trays: (project.cable_trays || []).filter((t) => t.id !== id),
  });
  const handleItemMove = (itemId, newX, newY, conflictMsgs) => {
    const next = (project.equipment_list || []).map((it) => {
      if (it.id !== itemId || !it.position) return it;
      const p = it.position;
      // Use orientedDimensions so the synced w_in/h_in stay correct for
      // flipped equipment (their pos.w_in/h_in were stale before this fix).
      const _od = orientedDimensions(it);
      const cz = { x: newX, y: newY - 36, w: _od.wallSize, h: 36 };
      return { ...it, position: {
        ...p, x_in: newX, y_in: newY,
        w_in: _od.wallSize, h_in: _od.thickness,
        clearance: cz,
      }};
    });
    update({ equipment_list: next });
    setViolations(conflictMsgs);
  };

  /** Apply a click-to-edit dimension change. `side` is W/E/N/S, `newDist`
   *  is the new clearance to the blocker. Equipment shifts so the relevant
   *  edge sits at `newDist` from the blocker; for trays we translate the
   *  polyline's centerline / drop point. */
  const handleEditDimension = ({ side, currentDist, newDist }) => {
    if (!selection?.id || !Number.isFinite(newDist) || newDist < 0) return;
    const delta = newDist - currentDist;          // positive moves AWAY from blocker
    if (Math.abs(delta) < 0.5) return;            // no-op
    if (selection.kind === "equipment") {
      const it = (project.equipment_list || []).find((x) => x.id === selection.id);
      if (!it?.position) return;
      const p = it.position;
      // Use orientedDimensions so the clamp uses the CURRENT (post-flip)
      // bbox, not the stale w_in/h_in stored on position. Was: clamping
      // against p.w_in/h_in pinned flipped equipment to the WRONG corner.
      const _od = orientedDimensions(it);
      const wOriented = _od.wallSize;
      const hOriented = _od.thickness;
      let dx = 0, dy = 0;
      if (side === "W") dx = delta;
      if (side === "E") dx = -delta;
      if (side === "N") dy = delta;
      if (side === "S") dy = -delta;
      const newX = Math.max(0, Math.min((project.length_in || 240) - wOriented,
                                          Math.round(p.x_in + dx)));
      const newY = Math.max(0, Math.min((project.width_in  || 180) - hOriented,
                                          Math.round(p.y_in + dy)));
      const next = (project.equipment_list || []).map((x) =>
        x.id !== selection.id ? x : { ...x, position: {
          ...p, x_in: newX, y_in: newY,
          w_in: wOriented, h_in: hOriented,
        }});
      update({ equipment_list: next });
      setPreviewPos(null);
      setRecentlyEdited(selection.id);
      setTimeout(() => setRecentlyEdited((cur) => (cur === selection.id ? null : cur)), 700);
      toast.success(`${it.tag}: ${side} clearance = ${fmtLenShort(newDist, project)}`);
      return;
    }
    if (selection.kind === "tray") {
      const tray = (project.cable_trays || []).find((x) => x.id === selection.id);
      if (!tray) return;
      let dx = 0, dy = 0;
      if (side === "W") dx = delta;
      if (side === "E") dx = -delta;
      if (side === "N") dy = delta;
      if (side === "S") dy = -delta;
      const margin = wallFlushMargin(tray.width_in || 12);
      const L = project.length_in || 240, W = project.width_in || 180;
      const newPts = (tray.points || []).map(([x, y]) => [
        Math.max(margin, Math.min(L - margin, Math.round(x + dx))),
        Math.max(margin, Math.min(W - margin, Math.round(y + dy))),
      ]);
      const next = (project.cable_trays || []).map((x) =>
        x.id !== selection.id ? x : { ...x, points: newPts });
      update({ cable_trays: next });
      toast.success(`Tray: ${side} clearance = ${fmtLenShort(newDist, project)}`);
    }
  };

  /** v1.81 — Compute (but do NOT commit) the would-be position for a
   *  counter edit. Used by PlanDimensions to drive the live-preview glide
   *  as the user types. Passing `null` clears the preview. The math here
   *  MUST mirror handleEditDimension's commit path so the preview matches
   *  the final position pixel-for-pixel. */
  const handlePreviewDimension = (payload) => {
    if (!payload) { setPreviewPos(null); return; }
    if (!selection?.id) return;
    const { side, currentDist, newDist } = payload;
    if (!Number.isFinite(newDist) || newDist < 0) { setPreviewPos(null); return; }
    const delta = newDist - currentDist;
    if (selection.kind === "equipment") {
      const it = (project.equipment_list || []).find((x) => x.id === selection.id);
      if (!it?.position) return;
      const p = it.position;
      const _od = orientedDimensions(it);
      const wOriented = _od.wallSize;
      const hOriented = _od.thickness;
      let dx = 0, dy = 0;
      if (side === "W") dx = delta;
      if (side === "E") dx = -delta;
      if (side === "N") dy = delta;
      if (side === "S") dy = -delta;
      const newX = Math.max(0, Math.min((project.length_in || 240) - wOriented,
                                          Math.round(p.x_in + dx)));
      const newY = Math.max(0, Math.min((project.width_in  || 180) - hOriented,
                                          Math.round(p.y_in + dy)));
      setPreviewPos({ id: selection.id, x_in: newX, y_in: newY });
    }
    // Tray preview deferred — uncommon enough that the existing snap-on-
    // commit UX is acceptable. Can be added symmetrically if requested.
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="label-tag">// 03 Physical sizing</div>
          <h2 className="font-display text-2xl font-bold">Electrical room — size & layout</h2>
          <p className="text-sm text-gray-400 mt-1">Dimensions, NEC 110.26 auto-layout, drag-to-reposition, and NEC 392 cable tray routing.</p>
        </div>
        <div className="inline-flex border border-white/10">
          {["imperial", "metric"].map((s) => (
            <button key={s} onClick={() => setUnit(s)}
                    className={`px-3 py-1 text-xs font-mono ${project.unit_system === s ? "bg-[#00E5FF] text-black" : "text-gray-400 hover:text-white"}`}
                    data-testid={`size-unit-${s}`}>
              {s.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
        <Field label={`Weight (${unitWeight})`}>
          <input type="number" min={0}
            value={project.weight_lbs ? Number(wtOut(project.weight_lbs).toFixed(2)) : ""}
            placeholder="0"
            onChange={(e) => update({ weight_lbs: e.target.value === "" ? 0 : wtStore(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
            data-testid="size-weight" />
        </Field>
        <Field label={`Length (${unitLen})`}>
          <input type="number" min={0}
            value={project.length_in ? Number(lenOut(project.length_in).toFixed(metric ? 2 : 0)) : ""}
            placeholder="0"
            onChange={(e) => update({ length_in: e.target.value === "" ? 0 : lenStore(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none" data-testid="size-length" />
        </Field>
        <Field label={`Width (${unitLen})`}>
          <input type="number" min={0}
            value={project.width_in ? Number(lenOut(project.width_in).toFixed(metric ? 2 : 0)) : ""}
            placeholder="0"
            onChange={(e) => update({ width_in: e.target.value === "" ? 0 : lenStore(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none" data-testid="size-width" />
        </Field>
        <Field label={`Depth/Height (${unitLen})`}>
          <input type="number" min={0}
            value={project.depth_in ? Number(lenOut(project.depth_in).toFixed(metric ? 2 : 0)) : ""}
            placeholder="0"
            onChange={(e) => update({ depth_in: e.target.value === "" ? 0 : lenStore(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none" data-testid="size-depth" />
        </Field>
        {/* v2.00 — Wall thickness drives the visual depth of the enclosure
            shell in plan view + 3D, and feeds into clearance calculations.
            Default 6". User-editable. */}
        <Field label={`Wall thickness (${unitLen})`}>
          <input type="number" min={0} step={metric ? 0.01 : 0.5}
            value={(project.wall_thickness_in ?? 6) ? Number(lenOut(project.wall_thickness_in ?? 6).toFixed(metric ? 2 : 1)) : ""}
            placeholder="6"
            onChange={(e) => update({ wall_thickness_in: e.target.value === "" ? 0 : lenStore(e.target.value) })}
            title="Outer enclosure wall thickness. Drives the visual thickness of the shell in plan view + 3D."
            className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none" data-testid="size-wall-thickness" />
        </Field>
      </div>

      {/* v2.12 — Basement / sub-floor pit. Default 0 (no basement). When
          > 0, a violet sub-floor cavity is rendered below the floor in
          2D + 3D, and the "Basement" filter / placement mode become
          available. Use for power-floor cable tunnels, cable basements
          under switchgear lineups, raised-floor data-centre setups, etc. */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
        <Field label={`Basement / sub-floor depth (${unitLen})`}>
          <input type="number" min={0} step={metric ? 0.01 : 1}
            value={project.basement_depth_in ? Number(lenOut(project.basement_depth_in).toFixed(metric ? 2 : 0)) : ""}
            placeholder="0"
            onChange={(e) => update({ basement_depth_in: e.target.value === "" ? 0 : lenStore(e.target.value) })}
            title="Depth of the sub-floor / basement pit below the room floor. 0 = no basement. Used for cable basements, raised-floor data centres, switchgear power-floor cable tunnels."
            className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#A78BFA] outline-none"
            data-testid="size-basement-depth" />
        </Field>
        {project.basement_depth_in > 0 && (
          <div className="md:col-span-2 self-end text-[11px] text-[#A78BFA] font-mono">
            ✓ Basement enabled · switch the filter to <strong>Basement</strong> to view sub-floor items, or pick <em>Basement</em> before placing new <strong>equipment / trays</strong>.
          </div>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
        <div className="bg-[#0A0C10] p-5">
          <div className="label-tag">// Footprint</div>
          <div className="font-mono text-2xl text-[#00E5FF] mt-2" data-testid="size-footprint">
            {metric
              ? `${(footprint / (IN_PER_M * IN_PER_M)).toFixed(2)} m²`
              : `${footprint.toFixed(1)} in²`}
          </div>
          {!metric && footprint > 0 && (
            <div className="text-xs text-gray-500 font-mono mt-1">≈ {(footprint / 144).toFixed(1)} ft²</div>
          )}
        </div>
        <div className="bg-[#0A0C10] p-5">
          <div className="label-tag">// Volume</div>
          <div className="font-mono text-2xl text-[#00E5FF] mt-2">
            {(() => {
              const volIn3 = footprint * (project.depth_in || 0);
              return metric
                ? `${(volIn3 / Math.pow(IN_PER_M, 3)).toFixed(2)} m³`
                : `${(volIn3 / 1728).toFixed(1)} ft³`;
            })()}
          </div>
        </div>
        <div className="bg-[#0A0C10] p-5">
          <div className="label-tag">// Floor load</div>
          <div className="font-mono text-2xl text-[#00E5FF] mt-2">
            {(() => {
              if (footprint <= 0) return `— ${metric ? "kg/m²" : "lb/ft²"}`;
              if (metric) {
                const m2 = footprint / (IN_PER_M * IN_PER_M);
                const kg = (project.weight_lbs || 0) / LB_PER_KG;
                return `${(kg / m2).toFixed(0)} kg/m²`;
              }
              const ft2 = footprint / 144;
              return `${((project.weight_lbs || 0) / ft2).toFixed(0)} lb/ft²`;
            })()}
          </div>
          <div className="text-xs text-gray-500 font-mono mt-1">Approx. static load on slab</div>
        </div>
      </div>

      <div className="mt-8 border border-[#00E5FF]/30 bp-grid-strong p-6" data-testid="size-3d-preview">
        <div className="label-tag mb-3">// Iso preview</div>
        <Iso3D length={project.length_in || 100} width={project.width_in || 60} depth={project.depth_in || 96} />
      </div>

      <div className="mt-8 border border-[#00E5FF]/30 bg-[#0A0C10] p-6" data-testid="size-plan-view">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="label-tag">// Plan view · NEC 110.26 + 392</div>
            <h3 className="font-display text-xl font-bold">Layout & cable trays</h3>
            <p className="text-xs text-gray-400 mt-1">Drag equipment to reposition. Toggle &ldquo;Tray / conduit&rdquo; to add segments by clicking two points.</p>
          </div>
        </div>

        {/* v2.00 — Unified toolbar matching the Wall Elevations style:
            Undo / Redo / Pin layout · Tray-conduit / Tray-run / Equipment
            mode toggle · Interior/Exterior filter. */}
        <div className="flex items-center gap-2 flex-wrap font-mono mb-3"
             data-testid="plan-toolbar">
          <div className="relative inline-flex items-stretch border"
               style={{ borderColor: canUndo ? "rgba(0,229,255,0.4)" : "rgba(255,255,255,0.1)" }}
               data-testid="plan-undo-wrap">
            <button type="button"
                    onClick={onUndo}
                    disabled={!canUndo}
                    title={canUndo ? "Undo last change (Ctrl+Z)" : "Nothing to undo"}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs ${
                      canUndo ? "text-[#00E5FF] hover:bg-[#00E5FF]/10" : "text-gray-600 cursor-not-allowed"
                    }`}
                    data-testid="plan-undo-btn">
              <ArrowCounterClockwise size={12} weight="bold" />
              Undo
            </button>
            <button type="button"
                    onClick={onRedo}
                    disabled={!canRedo}
                    title={canRedo ? "Redo (Ctrl+Shift+Z)" : "Nothing to redo"}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs border-l ${
                      canRedo ? "border-[#00E5FF]/20 text-[#00E5FF] hover:bg-[#00E5FF]/10"
                              : "border-white/5 text-gray-600 cursor-not-allowed"
                    }`}
                    data-testid="plan-redo-btn">
              <ArrowClockwise size={12} weight="bold" />
              Redo
            </button>
          </div>
          <button type="button"
                  onClick={() => setPinDialogOpen(true)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#FFCC00]/40 text-[#FFCC00] hover:bg-[#FFCC00]/10"
                  title="Save current plan-view layout as a named checkpoint"
                  data-testid="plan-pin-btn">
            <PushPin size={12} weight="fill" />
            Pin layout {layoutPins.length > 0 && `(${layoutPins.length})`}
          </button>

          {/* Mode toggle matches Wall Elevations: Tray/conduit · Partition · Equipment(select) */}
          <div className="inline-flex border border-white/10"
               data-testid="plan-mode-toggle">
            <button type="button" onClick={() => setPlanMode("select")}
                    className={`px-3 py-1 text-xs ${planMode === "select"
                      ? "bg-[#00E5FF] text-black font-bold"
                      : "text-gray-400 hover:text-white"}`}
                    title="Select / drag equipment"
                    data-testid="plan-mode-select">
              Select
            </button>
            <button type="button" onClick={() => setPlanMode("tray")}
                    className={`px-3 py-1 text-xs border-l border-white/10 ${planMode === "tray"
                      ? "bg-[#FFCC00] text-black font-bold"
                      : "text-gray-400 hover:text-white"}`}
                    title="Draw tray / conduit — click two points to add"
                    data-testid="plan-mode-tray">
              Tray / conduit
            </button>
            <button type="button" onClick={() => setPlanMode("partition")}
                    disabled={(project.room_partitions || []).length >= 4}
                    className={`px-3 py-1 text-xs border-l border-white/10 ${planMode === "partition"
                      ? "bg-[#FF9F0A] text-black font-bold"
                      : "text-gray-400 hover:text-white"} disabled:opacity-40 disabled:cursor-not-allowed`}
                    title={(project.room_partitions || []).length >= 4
                      ? "Max 4 partitions per project"
                      : "Click 2 points in the plan to draw an interior partition"}
                    data-testid="plan-mode-partition">
              Partition ({(project.room_partitions || []).length}/4)
            </button>
            <button type="button" onClick={() => setPlanMode("equipment")}
                    disabled={unplacedItems.length === 0}
                    className={`px-3 py-1 text-xs border-l border-white/10 ${planMode === "equipment"
                      ? "bg-[#00E5FF] text-black font-bold"
                      : "text-gray-400 hover:text-white"} disabled:opacity-40 disabled:cursor-not-allowed`}
                    title={unplacedItems.length === 0
                      ? "All equipment is already placed. Add new equipment in the Equipment List tab first."
                      : installSide === "exterior"
                        ? "Pick an unplaced item, then click on the plan — placed on ROOF (exterior)"
                        : installSide === "basement"
                          ? "Pick an unplaced item, then click on the plan — placed in BASEMENT (sub-floor)"
                          : "Pick an unplaced item, then click on the plan to place it on the floor"}
                    data-testid="plan-mode-equipment">
              Equipment ({unplacedItems.length})
            </button>
          </div>

          {/* Equipment picker — appears when in Equipment mode */}
          {planMode === "equipment" && (
            unplacedItems.length === 0 ? (
              <div className="text-[10px] text-yellow-300 max-w-xs px-2 py-1 border border-yellow-300/30"
                   data-testid="plan-equip-no-unplaced">
                All equipment items are placed. Add new equipment in the
                Equipment List tab first.
              </div>
            ) : (
              <div className="inline-flex items-center gap-2">
                <select value={pickedItemId}
                        onChange={(e) => setPickedItemId(e.target.value)}
                        className="bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono focus:border-[#00E5FF] outline-none"
                        data-testid="plan-picked-item">
                  <option value="">— pick unplaced item —</option>
                  {unplacedItems.map((it) => (
                    <option key={it.id} value={it.id}>
                      {it.tag || "(no tag)"} · {it.equipment_type || "?"}
                      {it.length_in ? ` · ${it.length_in}×${it.width_in}″` : ""}
                    </option>
                  ))}
                </select>
                {pickedItem && (
                  <span className={`text-[10px] font-mono px-2 py-1 border ${
                    installSide === "exterior"
                      ? "border-[#FF9F0A]/50 text-[#FF9F0A] bg-[#FF9F0A]/10"
                      : installSide === "basement"
                        ? "border-[#A78BFA]/50 text-[#A78BFA] bg-[#A78BFA]/10"
                        : "border-[#00E5FF]/40 text-[#00E5FF] bg-[#00E5FF]/10"
                  }`}>
                    {installSide === "exterior"
                      ? `→ click plan to place on ROOF (exterior)`
                      : installSide === "basement"
                        ? `→ click plan to place in BASEMENT (sub-floor, ${project.basement_depth_in || 0}″ below)`
                        : `→ click plan to place on floor`}
                  </span>
                )}
              </div>
            )
          )}

          <button onClick={() => setShowDims(!showDims)}
                  className={`px-3 py-1 text-xs border ${
                    showDims ? "border-[#FFCC00]/60 text-[#FFCC00] bg-[#FFCC00]/10"
                             : "border-white/10 text-gray-400 hover:text-white"}`}
                  title="Show clearance arrows on the selected equipment"
                  data-testid="plan-toggle-dims">
            Dims: {showDims ? "on" : "off"}
          </button>

          {/* v2.09 — thick/thin walls toggle REMOVED at user request */}

          <div className="inline-flex items-center border border-white/10 bg-[#050608]"
               data-testid="install-side-filter">
            {[
              { v: "all",       label: "All",       enabled: true },
              { v: "interior",  label: "Interior",  enabled: true },
              { v: "exterior",  label: "Exterior",  enabled: true },
              { v: "basement",  label: "Basement",  enabled: (project.basement_depth_in || 0) > 0 },
            ].map((opt) => (
              <button key={opt.v}
                      onClick={() => opt.enabled && setInstallSide(opt.v)}
                      disabled={!opt.enabled}
                      className={`text-xs font-mono py-1 px-3 ${
                        !opt.enabled
                          ? "text-gray-600 cursor-not-allowed opacity-50"
                          : installSide === opt.v
                            ? (opt.v === "basement"
                                ? "bg-[#A78BFA]/20 text-[#A78BFA]"
                                : "bg-[#00E5FF]/20 text-[#00E5FF]")
                            : "text-gray-400 hover:text-[#00E5FF]"}`}
                      title={
                        opt.v === "basement" && !opt.enabled
                          ? "Set 'Basement / sub-floor depth' above 0 to enable basement placement"
                          : opt.v === "all"      ? "Show interior + exterior + through items"
                          : opt.v === "interior" ? "Show only equipment installed INSIDE the enclosure"
                          : opt.v === "exterior" ? "Show only equipment installed on the EXTERIOR wall face"
                                                 : "Show only BASEMENT (sub-floor) equipment"}
                      data-testid={`install-side-${opt.v}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Secondary action row — Help, Doors/Windows, Clear, Auto-layout, Auto-route */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="plan-action-row">
          <button onClick={() => setHelpOpen(true)} className="btn-ghost text-xs py-1 inline-flex items-center gap-2" data-testid="size-help"><Question size={12} /> Help</button>
          <button onClick={() => setOpeningsOpen(true)} className="btn-ghost text-xs py-1 inline-flex items-center gap-2" data-testid="size-doors-windows"><Door size={12} /> Doors/Windows</button>
          <button onClick={clearLayout} className="btn-ghost text-xs py-1" data-testid="layout-clear">Clear</button>
          <button onClick={runAutoLayout} disabled={busy} className="btn-ghost text-xs py-1 inline-flex items-center gap-2" data-testid="layout-auto"><Sparkle size={12} weight="fill" /> Auto-layout</button>
          <button onClick={runAutoTrays} disabled={busy} className="btn-ghost text-xs py-1 inline-flex items-center gap-2" data-testid="trays-auto"><Sparkle size={12} weight="fill" /> Auto-route tray</button>
        </div>

        {/* Pinned-layout chips */}
        {layoutPins.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3 font-mono text-[10px]"
               data-testid="plan-pin-chips">
            <span className="text-gray-500">Pinned:</span>
            {layoutPins.map((pin) => (
              <span key={pin.id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#FFCC00]/40 bg-[#FFCC00]/5">
                <button onClick={() => restorePin(pin)}
                        className="text-[#FFCC00] hover:underline"
                        title={`Restore "${pin.name}" from ${new Date(pin.at).toLocaleString()}`}
                        data-testid={`plan-pin-restore-${pin.id}`}>
                  {pin.name}
                </button>
                <button onClick={() => setLayoutPins((arr) => arr.filter((p) => p.id !== pin.id))}
                        className="text-gray-500 hover:text-[#FF8A85] ml-1"
                        title="Remove pin"
                        data-testid={`plan-pin-remove-${pin.id}`}>×</button>
              </span>
            ))}
          </div>
        )}

        {/* Simple pin-name dialog (inline) */}
        {pinDialogOpen && (
          <div className="mb-3 border border-[#FFCC00]/40 bg-[#FFCC00]/5 p-3 flex items-center gap-2"
               data-testid="plan-pin-dialog">
            <PushPin size={14} weight="fill" className="text-[#FFCC00]" />
            <input type="text" autoFocus value={pinName} onChange={(e) => setPinName(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") pinCurrent(); if (e.key === "Escape") setPinDialogOpen(false); }}
                   placeholder="Pin name (e.g. baseline · option-A)"
                   className="flex-1 bg-black/40 border border-white/10 px-2 py-1 text-sm font-mono focus:border-[#FFCC00] outline-none"
                   data-testid="plan-pin-name" />
            <button onClick={pinCurrent} disabled={!pinName.trim()}
                    className="px-3 py-1 text-xs border border-[#FFCC00] bg-[#FFCC00]/10 text-[#FFCC00] hover:bg-[#FFCC00]/20 disabled:opacity-40"
                    data-testid="plan-pin-save">Pin checkpoint</button>
            <button onClick={() => { setPinDialogOpen(false); setPinName(""); }}
                    className="px-3 py-1 text-xs border border-white/15 text-gray-300 hover:text-white"
                    data-testid="plan-pin-cancel">Cancel</button>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 border border-white/10 p-3 bg-[#050608]">
          <div>
            <div className="label-tag">// Tray type</div>
            <select value={trayDefaults.tray_type} onChange={(e) => setTrayDefaults({ tray_type: e.target.value })}
              className="mt-1 w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#FFCC00] outline-none" data-testid="tray-type">
              {(trayOptions?.types || ["Ladder", "Solid Bottom", "Ventilated Trough", "Wire Mesh", "Channel"]).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div className="label-tag">// Width (in)</div>
            <select value={trayDefaults.width_in} onChange={(e) => setTrayDefaults({ width_in: Number(e.target.value) })}
              className="mt-1 w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#FFCC00] outline-none" data-testid="tray-width">
              {(trayOptions?.widths_in || [4, 6, 12, 18, 24, 36]).map((w) => <option key={w} value={w}>{`${w}"`}</option>)}
            </select>
          </div>
          <div>
            <div className="label-tag">// Height (in)</div>
            <select value={trayDefaults.height_in} onChange={(e) => setTrayDefaults({ height_in: Number(e.target.value) })}
              className="mt-1 w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#FFCC00] outline-none" data-testid="tray-height">
              {(trayOptions?.heights_in || [3, 4, 5, 6]).map((h) => <option key={h} value={h}>{`${h}"`}</option>)}
            </select>
          </div>
          <div>
            <div className="label-tag">// Material</div>
            <select value={trayDefaults.material} onChange={(e) => setTrayDefaults({ material: e.target.value })}
              className="mt-1 w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#FFCC00] outline-none" data-testid="tray-material">
              {(trayOptions?.materials || ["Aluminum", "Galvanized Steel", "Stainless Steel", "FRP"]).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <div className="label-tag">// Fill limit (%)</div>
            <input type="number" min={20} max={100} value={trayDefaults.fill_limit_pct} onChange={(e) => setTrayDefaults({ fill_limit_pct: Number(e.target.value) })}
              className="mt-1 w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#FFCC00] outline-none" data-testid="tray-fill" />
          </div>
        </div>

        {/* v2.11 — partition draw hint */}
        {partitionMode && (
          <div className="mb-3 border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 px-3 py-2 text-[11px] font-mono text-[#FF9F0A]"
               data-testid="partition-draw-hint">
            <strong>Drawing partition:</strong> click two points to add. Default is <strong>straight (orthogonal)</strong> — hold <kbd className="px-1 py-0.5 bg-black/40 border border-[#FF9F0A]/40">Shift</kbd> for free-form diagonal. Minimum length 12″. Right-click an existing partition to delete it; click to select and edit doors / windows / height. <kbd className="px-1 py-0.5 bg-black/40 border border-[#FF9F0A]/40">Esc</kbd> to cancel.
          </div>
        )}

        {/* v2.13 — Basement mode hint. Visible whenever the user has
            switched the install-side filter to Basement. Tells them
            exactly how to place equipment + trays into the sub-floor pit. */}
        {installSide === "basement" && (project.basement_depth_in || 0) > 0 && (
          <div className="mb-3 border border-[#A78BFA]/40 bg-[#A78BFA]/5 px-3 py-2 text-[11px] font-mono text-[#A78BFA]"
               data-testid="basement-mode-hint">
            <strong>Basement mode</strong> · sub-floor depth {lenOut(project.basement_depth_in).toFixed(metric ? 2 : 0)} {unitLen}.
            New <strong>equipment</strong> placed via <em>Equipment</em> mode lands in the pit (mount_surface=basement, mount_height_in=−{project.basement_depth_in}″).
            New <strong>cable trays</strong> drawn via <em>Tray / conduit</em> mode are also routed in the basement.
            Existing items above the floor are hidden — switch back to <em>All</em> or <em>Interior</em> to view them.
          </div>
        )}

        <PlanView project={project} onItemMove={handleItemMove} trayMode={trayMode}
          partitionMode={partitionMode}
          equipmentMode={equipmentMode}
          pickedItem={pickedItem}
          onEquipmentPlace={handlePlanEquipmentPlace}
          showDims={showDims}
          installSide={installSide}
          thickWalls={thickWalls}
          selection={selection} setSelection={setSelection}
          onEditDimension={handleEditDimension}
          onPreviewDimension={handlePreviewDimension}
          previewPos={previewPos}
          recentlyEdited={recentlyEdited}
          onTrayEndMove={(trayId, endpoint, newX, newY) => {
            // v1.87 — patch the corresponding endpoint in cable_trays.
            const list = (project.cable_trays || []).map((t) => {
              if (t.id !== trayId || !Array.isArray(t.points) || t.points.length < 2) return t;
              const idx = endpoint === "A" ? 0 : 1;
              const newPts = t.points.map((p, i) => (i === idx ? [newX, newY] : p));
              return { ...t, points: newPts };
            });
            update({ cable_trays: list });
          }}
          onPartitionAdd={(seg) => {
            const list = [...(project.room_partitions || []), seg];
            update({ room_partitions: list });
            setPartitionMode(false);
            toast.success(`Partition added (${list.length}/4)`);
          }}
          onPartitionUpdate={(id, patch) => {
            const list = (project.room_partitions || []).map((p) =>
              p.id === id ? { ...p, ...patch } : p);
            update({ room_partitions: list });
          }}
          onPartitionDelete={(id) => {
            const list = (project.room_partitions || []).filter((p) => p.id !== id);
            update({ room_partitions: list });
            if (selection?.kind === "partition" && selection.id === id) {
              setSelection({ kind: null, id: null });
            }
            toast.success("Partition deleted");
          }}
          onTrayAdd={(seg) => {
            // v2.13 — propagate the current install-side filter so trays
            // added while Basement is active land in the sub-floor pit
            // (mount_surface=basement, installation_side=basement). Same
            // pattern for Exterior → ROOF.
            const trayInstSide =
              installSide === "basement" ? "basement"
              : installSide === "exterior" ? "exterior"
              : "interior";
            const trayMountSurface =
              installSide === "basement" ? "basement"
              : installSide === "exterior" ? "roof"
              : "floor";
            const enriched = { ...seg, installation_side: trayInstSide, mount_surface: trayMountSurface };
            update({ cable_trays: [...(project.cable_trays || []), enriched] });
            setTrayMode(false);
            const where = installSide === "basement" ? "BASEMENT" : installSide === "exterior" ? "ROOF" : "floor";
            toast.success(`Cable tray added on ${where}`);
          }} />

        {/* v2.11 — Partition properties panel. Visible whenever a partition
            is selected in the plan view. Lets the user edit height_in (Z),
            door dimensions/offset, add/remove windows, or delete. */}
        {selection?.kind === "partition" && (() => {
          const part = (project.room_partitions || []).find((p) => p.id === selection.id);
          if (!part) return null;
          // v2.13 — read doors as unified array (migrate legacy single door).
          const doorsArr = Array.isArray(part.doors) && part.doors.length
            ? part.doors
            : (part.door ? [{ id: "door-legacy", ...part.door }] : []);
          const idx = (project.room_partitions || []).indexOf(part);
          const segLabel = `SEG-${String(idx + 1).padStart(2, "0")}`;
          const lenIn = Math.hypot(part.x2 - part.x1, part.y2 - part.y1);
          const patchPart = (patch) => {
            const list = (project.room_partitions || []).map((p) =>
              p.id === part.id ? { ...p, ...patch } : p);
            update({ room_partitions: list });
          };
          // v2.13 — doors patch helpers (mirror the windows helpers below).
          const patchDoorAt = (did, patch) => {
            const next = doorsArr.map((d) => (d.id || `door-legacy`) === did ? { ...d, ...patch } : d);
            // Drop the legacy `door` field once we touch the array so the
            // canonical shape is `doors:[…]` from now on.
            patchPart({ doors: next, door: null });
          };
          const addDoor = () => {
            const newDoor = {
              id: `door-${Date.now()}`,
              offset: doorsArr.length === 0 ? 0.5 : 0.75,
              width_in: 36,
              height_in: 84,
            };
            patchPart({ doors: [...doorsArr, newDoor], door: null });
            toast.success(`Door added to ${segLabel}`);
          };
          const removeDoor = (did) => {
            patchPart({
              doors: doorsArr.filter((d) => (d.id || "door-legacy") !== did),
              door: null,
            });
          };
          const patchWindow = (wid, patch) => patchPart({
            windows: (part.windows || []).map((w) => w.id === wid ? { ...w, ...patch } : w),
          });
          const addWindow = () => {
            const newWin = {
              id: `win-${Date.now()}`,
              offset: 0.25,
              width_in: 24,
              height_in: 36,
              sill_in: 36,
            };
            patchPart({ windows: [...(part.windows || []), newWin] });
            toast.success(`Window added to ${segLabel}`);
          };
          const removeWindow = (wid) => {
            patchPart({ windows: (part.windows || []).filter((w) => w.id !== wid) });
          };
          return (
            <div className="mt-4 border border-[#FF9F0A]/40 bg-[#050608] p-4"
                 data-testid="partition-props-panel">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div>
                  <div className="label-tag">// Partition · {segLabel}</div>
                  <div className="font-mono text-xs text-gray-400 mt-1">
                    A=({part.x1},{part.y1})″ → B=({part.x2},{part.y2})″ · length {fmtLenShort(lenIn, project)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelection({ kind: null, id: null })}
                    className="px-3 py-1 text-xs border border-white/15 text-gray-300 hover:text-white"
                    data-testid="partition-deselect">
                    Done
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete partition ${segLabel}?`)) {
                        const list = (project.room_partitions || []).filter((p) => p.id !== part.id);
                        update({ room_partitions: list });
                        setSelection({ kind: null, id: null });
                        toast.success("Partition deleted");
                      }
                    }}
                    className="px-3 py-1 text-xs border border-[#FF4444]/50 bg-[#FF4444]/10 text-[#FF8A85] hover:bg-[#FF4444]/20 inline-flex items-center gap-1"
                    data-testid="partition-delete-btn">
                    <Trash size={12} /> Delete partition
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Field label={`Height / Z (${unitLen})`}>
                  <input type="number" min={12} step={metric ? 0.1 : 1}
                    value={part.height_in ? Number(lenOut(part.height_in).toFixed(metric ? 2 : 0)) : ""}
                    onChange={(e) => patchPart({ height_in: e.target.value === "" ? 96 : lenStore(e.target.value) })}
                    className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#FF9F0A] outline-none"
                    data-testid="partition-height" />
                </Field>
                <Field label="X1 / Y1 (in)">
                  <div className="flex gap-1">
                    <input type="number"
                      value={part.x1}
                      onChange={(e) => patchPart({ x1: Math.max(0, Math.min(project.length_in || 0, Number(e.target.value) || 0)) })}
                      className="w-1/2 bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#FF9F0A] outline-none"
                      data-testid="partition-x1" />
                    <input type="number"
                      value={part.y1}
                      onChange={(e) => patchPart({ y1: Math.max(0, Math.min(project.width_in || 0, Number(e.target.value) || 0)) })}
                      className="w-1/2 bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#FF9F0A] outline-none"
                      data-testid="partition-y1" />
                  </div>
                </Field>
                <Field label="X2 / Y2 (in)">
                  <div className="flex gap-1">
                    <input type="number"
                      value={part.x2}
                      onChange={(e) => patchPart({ x2: Math.max(0, Math.min(project.length_in || 0, Number(e.target.value) || 0)) })}
                      className="w-1/2 bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#FF9F0A] outline-none"
                      data-testid="partition-x2" />
                    <input type="number"
                      value={part.y2}
                      onChange={(e) => patchPart({ y2: Math.max(0, Math.min(project.width_in || 0, Number(e.target.value) || 0)) })}
                      className="w-1/2 bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#FF9F0A] outline-none"
                      data-testid="partition-y2" />
                  </div>
                </Field>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      // Snap to nearest axis using current dx/dy.
                      const ddx = Math.abs(part.x2 - part.x1);
                      const ddy = Math.abs(part.y2 - part.y1);
                      if (ddx >= ddy) patchPart({ y2: part.y1 });
                      else            patchPart({ x2: part.x1 });
                      toast.success("Snapped to straight line");
                    }}
                    className="w-full px-2 py-2 text-xs border border-[#FF9F0A]/40 text-[#FF9F0A] hover:bg-[#FF9F0A]/10"
                    data-testid="partition-snap-straight">
                    Snap to straight
                  </button>
                </div>
              </div>

              {/* Doors — v2.13: array (add / remove like windows). */}
              <div className="border border-white/10 p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-tag text-[#FF9F0A]">// Doors ({doorsArr.length})</div>
                  <button
                    onClick={addDoor}
                    className="px-2 py-1 text-xs border border-[#FF9F0A]/40 text-[#FF9F0A] hover:bg-[#FF9F0A]/10"
                    data-testid="partition-add-door">
                    + Add door
                  </button>
                </div>
                {doorsArr.length === 0 && (
                  <div className="text-xs text-gray-500 font-mono">
                    No doors — this partition is a solid wall. Cable trays and conduits can still penetrate the partition via individual wall penetrations (added separately via the partition's <em>penetrations</em> field).
                  </div>
                )}
                {doorsArr.map((d, di) => {
                  const did = d.id || "door-legacy";
                  return (
                    <div key={did} className="grid grid-cols-4 gap-2 mb-2 items-end"
                         data-testid={`partition-door-${di}`}>
                      <Field label="Offset (0-1)">
                        <input type="number" step={0.05} min={0} max={1}
                          value={d.offset ?? 0.5}
                          onChange={(e) => patchDoorAt(did, { offset: Math.max(0, Math.min(1, Number(e.target.value) || 0)) })}
                          className="w-full bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#FF9F0A] outline-none"
                          data-testid={`partition-door-${di}-offset`} />
                      </Field>
                      <Field label={`Width (${unitLen})`}>
                        <input type="number" min={1} step={metric ? 0.01 : 1}
                          value={Number(lenOut(d.width_in || 36).toFixed(metric ? 2 : 0))}
                          onChange={(e) => patchDoorAt(did, { width_in: lenStore(e.target.value) })}
                          className="w-full bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#FF9F0A] outline-none"
                          data-testid={`partition-door-${di}-width`} />
                      </Field>
                      <Field label={`Height (${unitLen})`}>
                        <input type="number" min={1} step={metric ? 0.01 : 1}
                          value={Number(lenOut(d.height_in || 84).toFixed(metric ? 2 : 0))}
                          onChange={(e) => patchDoorAt(did, { height_in: lenStore(e.target.value) })}
                          className="w-full bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#FF9F0A] outline-none"
                          data-testid={`partition-door-${di}-height`} />
                      </Field>
                      <button
                        onClick={() => removeDoor(did)}
                        className="h-[34px] px-2 text-xs border border-[#FF4444]/40 text-[#FF8A85] hover:bg-[#FF4444]/10 inline-flex items-center justify-center gap-1"
                        data-testid={`partition-door-${di}-delete`}
                        title="Remove this door">
                        <Trash size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Windows */}
              <div className="border border-white/10 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-tag text-[#00E5FF]">// Windows ({(part.windows || []).length})</div>
                  <button
                    onClick={addWindow}
                    className="px-2 py-1 text-xs border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10"
                    data-testid="partition-add-window">
                    + Add window
                  </button>
                </div>
                {(part.windows || []).length === 0 && (
                  <div className="text-xs text-gray-500 font-mono">No windows on this partition.</div>
                )}
                {(part.windows || []).map((w, wi) => (
                  <div key={w.id} className="grid grid-cols-5 gap-2 mb-2 items-end"
                       data-testid={`partition-window-${wi}`}>
                    <Field label={`Offset`}>
                      <input type="number" step={0.05} min={0} max={1}
                        value={w.offset ?? 0.5}
                        onChange={(e) => patchWindow(w.id, { offset: Math.max(0, Math.min(1, Number(e.target.value) || 0)) })}
                        className="w-full bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none" />
                    </Field>
                    <Field label={`Width (${unitLen})`}>
                      <input type="number" min={1} step={metric ? 0.01 : 1}
                        value={Number(lenOut(w.width_in || 24).toFixed(metric ? 2 : 0))}
                        onChange={(e) => patchWindow(w.id, { width_in: lenStore(e.target.value) })}
                        className="w-full bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none" />
                    </Field>
                    <Field label={`Height (${unitLen})`}>
                      <input type="number" min={1} step={metric ? 0.01 : 1}
                        value={Number(lenOut(w.height_in || 36).toFixed(metric ? 2 : 0))}
                        onChange={(e) => patchWindow(w.id, { height_in: lenStore(e.target.value) })}
                        className="w-full bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none" />
                    </Field>
                    <Field label={`Sill (${unitLen})`}>
                      <input type="number" min={0} step={metric ? 0.01 : 1}
                        value={Number(lenOut(w.sill_in || 36).toFixed(metric ? 2 : 0))}
                        onChange={(e) => patchWindow(w.id, { sill_in: lenStore(e.target.value) })}
                        className="w-full bg-black/40 border border-white/10 px-2 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none" />
                    </Field>
                    <button
                      onClick={() => removeWindow(w.id)}
                      className="h-[34px] px-2 text-xs border border-[#FF4444]/40 text-[#FF8A85] hover:bg-[#FF4444]/10 inline-flex items-center justify-center gap-1"
                      data-testid={`partition-window-${wi}-delete`}>
                      <Trash size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        <ElevationViews project={project} update={update} installSide={installSide} />

        {(layoutMeta || project.layout_meta) && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-xs">
            <Stat2 label="Voltage" value={`${project.voltage}V`} />
            <Stat2 label="Working space" value={`${(layoutMeta || project.layout_meta).working_space_in}\" (Cond 1)`} accent />
            <Stat2 label="Headroom req." value={`78\"`} />
            <Stat2 label="Two exits" value={(layoutMeta || project.layout_meta).two_exits_required ? "REQUIRED (>1200A)" : "Not required"} warn={(layoutMeta || project.layout_meta).two_exits_required} />
          </div>
        )}

        {(project.cable_trays || []).length > 0 && (
          <div className="mt-4 border border-[#FFCC00]/40 bg-[#050608] p-3" data-testid="tray-list">
            <div className="label-tag mb-2">// Tray segments ({(project.cable_trays || []).length})</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 font-mono text-xs">
              {(project.cable_trays || []).map((t, i) => {
                const idLabel = t.id_display || (t.id ? t.id.slice(0, 6) : `#${String(i + 1).padStart(2, "0")}`);
                const isDrop = t.kind === "drop" || !t.points || t.points.length < 2;
                const fmtPt = (pt) => pt ? `(${pt[0].toFixed(0)},${pt[1].toFixed(0)})` : "(?, ?)";
                return (
                  <div key={t.id} className="border border-white/10 p-2 flex items-center justify-between gap-2">
                    <div className="truncate text-gray-300">
                      <span className="text-[#FFCC00]">{idLabel}</span> · {t.kind || "run"} ·{" "}
                      {isDrop
                        ? `${fmtPt(t.points?.[0])} · ${t.bottom_in ?? 0}″→${t.top_in ?? 0}″`
                        : `${fmtPt(t.points?.[0])} → ${fmtPt(t.points?.[1])}`}
                    </div>
                    <button onClick={() => removeTray(t.id)} className="text-gray-500 hover:text-red-400" data-testid={`tray-del-${i}`}><Trash size={14} /></button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {violations.length > 0 && (
          <div className="mt-4 border border-[#FFCC00]/50 bg-[#1a1500] p-3 text-[#FFCC00] text-xs font-mono space-y-1" data-testid="layout-violations">
            {violations.map((v, i) => (<div key={`v-${i}-${(v || "").slice(0, 24)}`}>⚠ {v}</div>))}
          </div>
        )}
      </div>
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      <OpeningsEditor
        open={openingsOpen}
        onClose={() => setOpeningsOpen(false)}
        openings={project.wall_openings || []}
        onChange={(list) => update({ wall_openings: list })}
      />
    </div>
  );
}
